import { DateTime } from "luxon";
import {
  InvestorDiligenceStatus,
  PaymentMethod,
  PaymentMethodEnum,
  VerificationStatus,
} from "#graphql/supergraphEnums";
import { type CurrencyCode } from "#graphql/supergraphTypes";
import { toDbId } from "#utils/graphql_utils";
import { MoneyType } from "../../../../Money";
import { getMoneyFromWallet } from "../../../../Wallet/utils";
import { DEFAULT_PAYMENT_CAPABILITIES, PaymentCapabilities } from "../../../types";
import { isPaymentMethodSupported, stringToPaymentMethod } from "../../../utils";
import { PositionDistribution } from "../types";
import {
  InvalidPaymentInstructionReason,
  MAX_REFERENCE_TO_BENEFICIARY_LENGTH,
  PositionTabStatus,
  UnableToPayReason,
} from "./constants";
import { PositionRowData, UnableToPayReasonsObject } from "./types";

export function getPaymentMethodDisplay(paymentMethod?: PaymentMethodEnum | string | null) {
  const normalizedPaymentMethod = paymentMethod?.toLowerCase();
  if (normalizedPaymentMethod === PaymentMethodEnum.Wire) {
    return "Wire transfer";
  } else if (normalizedPaymentMethod === PaymentMethodEnum.Check) {
    return "Check";
  } else if (normalizedPaymentMethod === PaymentMethodEnum.Ach) {
    return "ACH";
  } else {
    return "";
  }
}

export function getPositionRowData(
  positionDistributions: PositionDistribution[],
  entityCurrency: CurrencyCode,
  paymentsInstructionScreening: boolean = false,
  paymentCapabilities: PaymentCapabilities = DEFAULT_PAYMENT_CAPABILITIES,
): PositionRowData[] {
  return positionDistributions.map((distribution) => {
    const netPayment = getMoneyFromWallet(distribution.amountPayable, entityCurrency);

    const unableToPayReason = getUnableToPayReason({
      positionDistribution: distribution,
      paymentsInstructionScreening,
      netPayment,
      paymentCapabilities,
    });

    const paid = distribution.hasPayment || distribution.hasAutomatedPayment;
    let positionTabStatus = PositionTabStatus.Available;
    if (paid) {
      positionTabStatus = PositionTabStatus.Paid;
    } else if (unableToPayReason) {
      positionTabStatus = PositionTabStatus.UnableToPay;
    }

    return {
      positionId: toDbId(distribution.position?.id || ""),
      distributionId: toDbId(distribution.id),
      paymentPrefId: toDbId(distribution.position?.paymentPref?.id || ""),
      accountName: distribution.position?.account?.gpTitle || "No GP Title found",
      paymentInstructions: distribution.position?.paymentPref?.bankInfoRedacted || "",
      paymentMethod: distribution.position?.paymentPref?.paymentMethod || "",
      grossAmount: getMoneyFromWallet(distribution.amountDistributed, entityCurrency),
      adjustments: getMoneyFromWallet(distribution.netAmountAdjustments, entityCurrency),
      netPayment,
      paid,
      positionTabStatus,
      unableToPayReason,
      paymentInstructionsRecentlyUpdated: getPaymentInstructionsRecentlyUpdated(distribution),
    };
  });
}

export const getUnableToPayReason = ({
  positionDistribution,
  paymentsInstructionScreening = false,
  netPayment,
  paymentCapabilities = DEFAULT_PAYMENT_CAPABILITIES,
}: {
  positionDistribution: PositionDistribution;
  paymentsInstructionScreening?: boolean;
  netPayment: MoneyType;
  paymentCapabilities?: PaymentCapabilities;
}): UnableToPayReasonsObject | null => {
  const paymentPref = positionDistribution.position?.paymentPref;

  if (positionDistribution.position?.hasPendingPaymentPrefChangeRequests) {
    return {
      unableToPayReason: UnableToPayReason.PendingPaymentInstructionChanges,
    };
  }

  if (positionDistribution.position?.hasPassedPicrComplianceChecks === false) {
    return {
      unableToPayReason: UnableToPayReason.PendingPicrComplianceChecks,
    };
  }

  if (!paymentPref) {
    return { unableToPayReason: UnableToPayReason.NoPaymentInstructions };
  }

  const paymentMethod = stringToPaymentMethod(paymentPref.paymentMethod);

  // Check if the payment method is supported based on the bank account's payment capabilities
  if (!paymentMethod || !isPaymentMethodSupported(paymentMethod, paymentCapabilities)) {
    return { unableToPayReason: UnableToPayReason.UnsupportedMethod };
  }

  if (paymentPref.verificationStatus === VerificationStatus.Error) {
    return {
      unableToPayReason: UnableToPayReason.InvalidInstructions,
      invalidPaymentInstructionReason: InvalidPaymentInstructionReason.General,
    };
  }

  if (netPayment.amount <= 0) {
    return { unableToPayReason: UnableToPayReason.InvalidAmount };
  }

  if (
    paymentMethod === PaymentMethod.Wire &&
    paymentPref.paymentPrefFull?.__typename === "WirePaymentPrefObjectType"
  ) {
    // Wire Payment Pref specific checks
    if (
      paymentPref.paymentPrefFull?.referenceToBeneficiary &&
      paymentPref.paymentPrefFull?.referenceToBeneficiary?.length >
        MAX_REFERENCE_TO_BENEFICIARY_LENGTH
    ) {
      return {
        unableToPayReason: UnableToPayReason.InvalidInstructions,
        invalidPaymentInstructionReason:
          InvalidPaymentInstructionReason.ReferenceToBeneficiaryLength,
      };
    }

    const wireBbi = paymentPref.paymentPrefFull.wireBbi;
    if (wireBbi?.field0 || wireBbi?.field1 || wireBbi?.field2 || wireBbi?.field3) {
      return {
        unableToPayReason: UnableToPayReason.InvalidInstructions,
        invalidPaymentInstructionReason: InvalidPaymentInstructionReason.BBI,
      };
    }
  }

  if (
    paymentsInstructionScreening &&
    (!paymentPref.watchlistStatus ||
      paymentPref.watchlistStatus.length === 0 ||
      paymentPref.watchlistStatus.some(
        (diligence) => diligence.status !== InvestorDiligenceStatus.Confirmed,
      ))
  ) {
    return {
      unableToPayReason: UnableToPayReason.PendingScreeningReview,
    };
  }

  return null;
};

export const getPaymentInstructionsRecentlyUpdated = (
  positionDistribution: PositionDistribution,
) => {
  const paymentPrefUpdatedAt = positionDistribution.position?.paymentPrefUpdatedAt as string;
  if (!paymentPrefUpdatedAt) {
    return false;
  }

  const oneDayAgo = DateTime.now().minus({ days: 1 });
  return DateTime.fromISO(paymentPrefUpdatedAt) > oneDayAgo;
};

/**
 * A helper function to sort positions such that positions with payment instructions
 * that have been recently updated are at the top.
 */
export const sortByPaymentInstructionsUpdated = (
  a: { paymentInstructionsRecentlyUpdated?: boolean | null },
  b: { paymentInstructionsRecentlyUpdated?: boolean | null },
) => {
  if (a.paymentInstructionsRecentlyUpdated && !b.paymentInstructionsRecentlyUpdated) {
    return -1;
  }
  if (!a.paymentInstructionsRecentlyUpdated && b.paymentInstructionsRecentlyUpdated) {
    return 1;
  }
  return 0;
};
